/**
 * Admin Activity Log - PTT transmission history
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Download, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import axios from 'axios';

export default function ActivityLog() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', userId: '', channelId: '' });
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo + 'T23:59:59';
      if (filters.userId) params.userId = filters.userId;
      if (filters.channelId) params.channelId = filters.channelId;
      const res = await axios.get('/api/admin/activity', { params });
      setData(res.data.data);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  // Load filter options once
  useEffect(() => {
    axios.get('/api/users').then(r => setUsers(r.data)).catch(() => {});
    axios.get('/api/channels').then(r => setChannels(r.data)).catch(() => {});
  }, []);

  const exportCSV = async () => {
    try {
      const params = { page: 1, limit: 10000 };
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo + 'T23:59:59';
      if (filters.userId) params.userId = filters.userId;
      if (filters.channelId) params.channelId = filters.channelId;
      const res = await axios.get('/api/admin/activity', { params });
      const rows = res.data.data;
      const csv = [
        ['ID', 'Usuario', 'Canal', 'Duración (s)', 'Fecha'],
        ...rows.map(r => [
          r.id,
          r.User?.username || r.userId,
          r.Channel?.name || r.channelId,
          r.duration,
          new Date(r.startedAt).toISOString(),
        ])
      ].map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transmisiones_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Error al exportar'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'medium' }) : '—';
  const formatDur = (s) => {
    if (!s) return '0s';
    if (s < 60) return `${Math.round(s * 10) / 10}s`;
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-n-800">Log de Actividad</h1>
          <p className="text-sm text-n-500 mt-0.5">{total} transmisiones registradas</p>
        </div>
        <button onClick={exportCSV}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
          style={{ background: 'rgba(52,199,89,0.12)', border: '1px solid rgba(52,199,89,0.25)', color: '#34C759' }}>
          <Download size={14} /> Exportar CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5 p-4 rounded-2xl"
        style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.05)' }}>
        <div className="flex items-center gap-2 text-xs text-n-500 mr-1">
          <Filter size={12} /> Filtros
        </div>
        <input type="date" value={filters.dateFrom}
          onChange={e => { setFilters(f => ({...f, dateFrom: e.target.value})); setPage(1); }}
          className="px-3 py-1.5 rounded-lg text-xs text-n-800 outline-none"
          style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
        />
        <span className="text-n-400 text-xs self-center">→</span>
        <input type="date" value={filters.dateTo}
          onChange={e => { setFilters(f => ({...f, dateTo: e.target.value})); setPage(1); }}
          className="px-3 py-1.5 rounded-lg text-xs text-n-800 outline-none"
          style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
        />
        <select value={filters.userId}
          onChange={e => { setFilters(f => ({...f, userId: e.target.value})); setPage(1); }}
          className="px-3 py-1.5 rounded-lg text-xs text-n-800 outline-none"
          style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}>
          <option value="">Todos los usuarios</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
        </select>
        <select value={filters.channelId}
          onChange={e => { setFilters(f => ({...f, channelId: e.target.value})); setPage(1); }}
          className="px-3 py-1.5 rounded-lg text-xs text-n-800 outline-none"
          style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}>
          <option value="">Todos los canales</option>
          {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {(filters.dateFrom || filters.dateTo || filters.userId || filters.channelId) && (
          <button onClick={() => { setFilters({ dateFrom: '', dateTo: '', userId: '', channelId: '' }); setPage(1); }}
            className="px-3 py-1.5 rounded-lg text-xs text-n-600 hover:text-n-800 transition-all"
            style={{ background: 'rgba(255,255,255,0.05)' }}>
            Limpiar
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-2xl overflow-hidden mb-4" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
        <table className="w-full">
          <thead>
            <tr style={{ background: '#F5F5F7', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              {['Usuario', 'Canal', 'Duración', 'Fecha / Hora'].map(h => (
                <th key={h} className="px-5 py-3 text-left text-xs font-bold text-n-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4" className="px-5 py-10 text-center text-n-400">Cargando...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan="4" className="px-5 py-10 text-center text-n-400">Sin registros</td></tr>
            ) : data.map(tx => (
              <tr key={tx.id} className="hover:bg-black/[0.02] transition-colors"
                style={{ borderBottom: '1px solid #F5F5F7' }}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                      style={{ background: tx.User?.avatarColor || '#007AFF' }}>
                      {(tx.User?.username?.[0] || '?').toUpperCase()}
                    </div>
                    <span className="text-sm text-n-700">{tx.User?.username || '—'}</span>
                  </div>
                </td>
                <td className="px-5 py-3 text-sm text-n-600">{tx.Channel?.name || '—'}</td>
                <td className="px-5 py-3">
                  <span className="text-sm font-mono text-s-blue">{formatDur(tx.duration)}</span>
                </td>
                <td className="px-5 py-3 text-sm text-n-500">{formatDate(tx.startedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-n-500">
            Página {page} de {totalPages} · {total} registros
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 rounded-lg text-n-600 hover:text-n-800 disabled:opacity-30 transition-all"
              style={{ background: 'rgba(255,255,255,0.05)' }}>
              <ChevronLeft size={14} />
            </button>
            <span className="text-sm text-n-600 font-medium px-3 py-1.5 rounded-lg"
              style={{ background: 'rgba(0,0,0,0.06)' }}>
              {page}
            </span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-2 rounded-lg text-n-600 hover:text-n-800 disabled:opacity-30 transition-all"
              style={{ background: 'rgba(255,255,255,0.05)' }}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
